import React, { useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import domtoimage from "dom-to-image";
import jsPDF from "jspdf";
import { Space, Spin, Modal, Button } from "antd";
import styled from "styled-components";
import { useMediaQuery } from "react-responsive";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { PageHeader } from "../../component/Header";
import { PrimaryButton, SecondaryButton } from "../../component/Button";
import surveyData from "./question.json";
import SLEReportPage1 from "../../components/SLE/SLEReportPage1";
import SLEReportPage2 from "../../components/SLE/SLEReportPage2";

// dayjs 객체 / 문자열 모두 YYYY-MM-DD로
const fmtDate = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.split("T")[0];
  if (typeof d.format === "function") return d.format("YYYY-MM-DD");
  if (d.$d instanceof Date) {
    const dt = d.$d;
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${dt.getFullYear()}-${m}-${day}`;
  }
  return "";
};

// 점수 → 양호/주의/위험
const getEvaluation = (score, thresholds) => {
  if (score >= thresholds.good) return "양호";
  if (score >= thresholds.warning) return "주의";
  return "위험";
};

// 화면 결과 요약표의 판정 셀 색상
const screenEvalStyle = (ev) => ({
  color: ev === "양호" ? "#2e9e4f" : ev === "주의" ? "#e8920c" : "#e0352b",
  backgroundColor:
    ev === "양호" ? "#eaf7ee" : ev === "주의" ? "#fdf3e2" : "#fcebe9",
  fontWeight: 700,
});

// 응답 점수 → 표시 라벨 (question.answers와 대조해 복원)
const getAnswerLabel = (q, score) => {
  if (score === undefined || score === null) return "미응답";
  if (score === q.answers.yes) return "예";
  if (score === q.answers.no) return "아니요";
  if (score === q.answers.unknown) return "모르겠음";
  return "-";
};

// 평가 조합별 권고 문구 (HTML)
const generateRecommendationHTML = (
  totalEvaluation,
  categoryAEvaluation,
  categoryBEvaluation
) => {
  if (
    totalEvaluation === "위험" ||
    categoryAEvaluation === "위험" ||
    categoryBEvaluation === "위험"
  ) {
    return `
      본 아동은 언어 선별 검사결과 또래보다 느린 수준으로 아동의 언어발달 수준을
      파악하기 위하여 <b style="color: #e0352b;">전문적인 심화 언어평가를 권고드립니다.</b>
      언어발달 전문가와의 심층적인 언어평가 결과를 바탕으로 진행하는 컨설팅을 통해
      아동의 강점과 약점을 파악하고 이에 대한 전문적인 지원을 받으시길 바랍니다.
      본 아동은 심화언어검사를 통해 직접적인 언어발달 지원 및 치료 서비스를 받고,
      초등학교 생활과 학습을 성공적으로 준비할 수 있기를 바랍니다.
    `;
  }

  if (
    totalEvaluation === "주의" &&
    categoryAEvaluation === "주의" &&
    categoryBEvaluation === "주의"
  ) {
    return `
      본 아동은 언어 선별 검사결과 <span style="color: #e8920c;">언어 이해 및 표현, 말 및 초기 문해 영역</span>에서
      '주의'가 나와 또래에 비해 다소 느린 발달을 보이는 것으로 나타났습니다.
      아동의 발달이 잘 이루어지는지 관심을 갖고 면밀히 살펴봐 주시기 바랍니다.
      놀이와 책을 활용하여 정확한 소리로 낱말을 표현하고, 한글에 대한 관심을 갖도록
      촉진해주시기 바랍니다. 이후 아동의 언어 이해 및 표현, 말 및 초기문해 능력이
      지속적으로 발달하여 초등학교 생활과 학습을 성공적으로 준비할 수 있기를 바랍니다.
    `;
  }

  if (
    totalEvaluation === "양호" &&
    categoryAEvaluation === "주의" &&
    categoryBEvaluation === "양호"
  ) {
    return `
      본 아동은 언어 선별 검사결과 또래 수준으로 <span style="color: #2e9e4f;">양호한 언어발달</span>을 보입니다.
      하지만 <span style="color: #e8920c;">언어 이해 및 표현 영역</span>에서 '주의'로 나타났습니다.
      해당 영역에서 아동 발달이 잘 이루어지는지 관심을 갖고 면밀히 살펴봐 주시고,
      놀이와 책을 활용하여 언어 이해 및 표현이 향상될 수 있도록 촉진해주시기 바랍니다.
      이후 아동의 언어 이해 및 표현, 말 및 초기문해 능력이 지속적으로 발달하여
      초등학교 생활과 학습을 성공적으로 준비할 수 있기를 바랍니다.
    `;
  }

  if (
    totalEvaluation === "양호" &&
    categoryAEvaluation === "양호" &&
    categoryBEvaluation === "주의"
  ) {
    return `
      본 아동은 언어 선별 검사결과 또래 수준으로 <span style="color: #2e9e4f;">양호한 언어발달</span>을 보입니다.
      하지만 <span style="color: #e8920c;">말 및 초기문해 영역</span>에서 '주의'로 나타났습니다.
      해당 영역에서 아동 발달이 잘 이루어지는지 관심을 갖고 면밀히 살펴봐 주시고,
      놀이와 책을 활용하여 정확한 소리로 낱말을 표현하고, 한글에 대한 관심을 갖도록
      촉진해주시기 바랍니다. 이후 아동의 언어 이해 및 표현, 말 및 초기문해 능력이
      지속적으로 발달하여 초등학교 생활과 학습을 성공적으로 준비할 수 있기를 바랍니다.
    `;
  }

  return `
    본 아동은 언어 선별 검사결과 또래 수준으로 <span style="color: #2e9e4f;">양호한 언어발달</span>을 보입니다.
    계속해서 아동의 언어발달 촉진에 좋은 놀이와 책을 활용하여 적극적으로
    의사소통해주시기 바랍니다. 이후 아동의 언어 이해 및 표현, 말 및 초기문해
    능력이 지속적으로 발달하여 초등학교 생활과 학습을 성공적으로 준비할 수
    있기를 바랍니다.
  `;
};

// 서버에 SLE 분석 결과 + PDF 저장
const saveSleToServer = async (userId, payload, pdfBlob) => {
  const fd = new FormData();
  fd.append("user_id", userId);
  fd.append("client_name", payload.info.name || "");
  fd.append("client_birthday", fmtDate(payload.info.birthday));
  fd.append("client_gender", payload.info.gender || "");
  fd.append("analysis_date", fmtDate(payload.info.examdate));
  fd.append("category_a_score", payload.categoryAScore);
  fd.append("category_b_score", payload.categoryBScore);
  fd.append("total_evaluation", payload.totalEvaluation);
  fd.append("analysis_result", JSON.stringify(payload));
  fd.append(
    "pdf_path",
    pdfBlob,
    `${payload.info.name || "SLE"}_결과보고서.pdf`
  );

  const response = await fetch(
    `${process.env.REACT_APP_SERVER_URL}/api/saveSLE`,
    { method: "POST", body: fd }
  );
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.message || "저장에 실패했습니다.");
  }
  return result;
};

function useForceUpdate() {
  const [, setState] = useState();
  return useCallback(() => setState({}), []);
}

// 미리보기: A4 페이지를 scale로 축소해 보여줌
const PreviewPage = ({ scale, children }) => (
  <div
    style={{
      width: `${794 * scale}px`,
      height: `${1123 * scale}px`,
      overflow: "hidden",
      margin: "0 auto 20px",
      boxShadow: "0 2px 14px rgba(0,0,0,0.18)",
      borderRadius: "4px",
    }}
  >
    <div
      style={{
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        width: "794px",
      }}
    >
      {children}
    </div>
  </div>
);

function Analysis({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });
  const forceUpdate = useForceUpdate();
  const [isExporting, setIsExporting] = useState(false);
  const [saveModal, setSaveModal] = useState({ status: "idle" });
  const ref1 = useRef();
  const ref2 = useRef();
  const ref3 = useRef();

  const state = location.state || {};
  const categoryAScore = state.categoryAScore ?? 0;
  const categoryBScore = state.categoryBScore ?? 0;
  const info = state.info || {};
  const responses = state.responses || {};

  const totalScore = categoryAScore + categoryBScore;
  const totalEvaluation = getEvaluation(totalScore, { good: 24, warning: 20 });
  const categoryAEvaluation = getEvaluation(categoryAScore, {
    good: 10,
    warning: 7,
  });
  const categoryBEvaluation = getEvaluation(categoryBScore, {
    good: 14,
    warning: 12,
  });
  const recommendationHtml = generateRecommendationHTML(
    totalEvaluation,
    categoryAEvaluation,
    categoryBEvaluation
  );

  const categoryA = surveyData.questions
    .filter((q) => q.category === "언어 이해 및 표현")
    .map((q) => ({
      id: q.id,
      question: q.question,
      answerLabel: getAnswerLabel(q, responses[q.id]),
    }));
  const categoryB = surveyData.questions
    .filter((q) => q.category === "말 및 문해")
    .map((q) => ({
      id: q.id,
      question: q.question,
      answerLabel: getAnswerLabel(q, responses[q.id]),
    }));

  const reportProps = {
    info,
    categoryAScore,
    categoryBScore,
    totalScore,
    totalEvaluation,
    categoryAEvaluation,
    categoryBEvaluation,
    recommendationHtml,
  };

  const generatePDF = async () => {
    setIsExporting(true);
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = 210;

    await new Promise((r) => setTimeout(r, 500)); // 렌더/폰트 로딩 대기
    forceUpdate();

    const nodes = [ref1.current, ref2.current, ref3.current].filter(Boolean);
    try {
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const dataUrl = await domtoimage.toJpeg(node, {
          quality: 0.95,
          style: {
            transform: "scale(1)",
            transformOrigin: "top left",
            fontFamily: "Noto Sans KR",
          },
          width: node.scrollWidth,
          height: node.scrollHeight,
        });

        const img = new Image();
        img.src = dataUrl;
        await new Promise((res) => {
          img.onload = () => {
            const imgHeight = (img.height * pdfWidth) / img.width;
            if (i > 0) pdf.addPage();
            pdf.addImage(dataUrl, "JPEG", 0, 0, pdfWidth, imgHeight);
            res();
          };
        });
      }
      return pdf;
    } catch (err) {
      console.error("PDF 생성 오류:", err);
      return null;
    } finally {
      setIsExporting(false);
    }
  };

  const downloadPDF = async () => {
    const pdf = await generatePDF();
    if (pdf) pdf.save(`${info.name || "SLE"}_결과보고서.pdf`);
  };

  const handleSave = async () => {
    if (!user || !user.id) {
      setSaveModal({
        status: "error",
        message: "사용자 정보가 없어 저장할 수 없습니다. 다시 로그인해주세요.",
      });
      return;
    }
    setSaveModal({ status: "saving" });
    try {
      const pdf = await generatePDF();
      if (!pdf) {
        setSaveModal({ status: "error", message: "PDF 생성에 실패했습니다." });
        return;
      }
      const pdfBlob = pdf.output("blob");
      const payload = {
        info,
        responses,
        categoryAScore,
        categoryBScore,
        totalScore,
        totalEvaluation,
        categoryAEvaluation,
        categoryBEvaluation,
      };
      const result = await saveSleToServer(user.id, payload, pdfBlob);
      setSaveModal({ status: "success", dataId: result.dataId });
    } catch (err) {
      console.error("저장 오류:", err);
      setSaveModal({
        status: "error",
        message: err.message || "서버 요청에 실패했습니다.",
      });
    }
  };

  const closeSaveModal = () => setSaveModal({ status: "idle" });

  // state 없이 직접 접근(새로고침/URL)한 경우
  if (!location.state) {
    return (
      <Modal
        open
        closable={false}
        keyboard={false}
        maskClosable={false}
        title="결과 데이터가 없습니다"
        okText="확인"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={() => navigate("/sle/input", { replace: true })}
      >
        체크리스트를 먼저 작성해주세요.
      </Modal>
    );
  }

  const previewScale = isMobile ? 0.42 : 0.6;

  return (
    <div>
      <PageHeader title="결과 보고서" />

      <BarContainer $isMobile={isMobile}>
        <SideComponent align="flex-start">
          <ReportName>{info.name || "SLE"}_결과보고서.pdf</ReportName>
        </SideComponent>
        <SideComponent align="flex-end">
          <Space>
            <SecondaryButton size="large" onClick={handleSave}>
              저장하기
            </SecondaryButton>
            <PrimaryButton
              size="large"
              loading={isExporting}
              onClick={downloadPDF}
            >
              PDF 내려받기
            </PrimaryButton>
          </Space>
        </SideComponent>
      </BarContainer>

      <SummaryArea>
        <SummaryTitle>결과 요약</SummaryTitle>
        <SummaryTable>
          <thead>
            <tr>
              <th>총점</th>
              <th>언어 이해 및 표현</th>
              <th>말 및 문해</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{totalScore}점</td>
              <td>{categoryAScore}점</td>
              <td>{categoryBScore}점</td>
            </tr>
            <tr>
              <td style={screenEvalStyle(totalEvaluation)}>
                {totalEvaluation}
              </td>
              <td style={screenEvalStyle(categoryAEvaluation)}>
                {categoryAEvaluation}
              </td>
              <td style={screenEvalStyle(categoryBEvaluation)}>
                {categoryBEvaluation}
              </td>
            </tr>
          </tbody>
        </SummaryTable>
      </SummaryArea>

      <PreviewArea>
        <PreviewPage scale={previewScale}>
          <SLEReportPage1 {...reportProps} />
        </PreviewPage>
        <PreviewPage scale={previewScale}>
          <SLEReportPage2
            sectionTitle="언어 이해 및 표현"
            rows={categoryA}
            clientName={info.name}
            pageLabel="1/2"
          />
        </PreviewPage>
        <PreviewPage scale={previewScale}>
          <SLEReportPage2
            sectionTitle="말 및 문해"
            rows={categoryB}
            clientName={info.name}
            pageLabel="2/2"
          />
        </PreviewPage>
      </PreviewArea>

      {/* 캡처용 풀사이즈 (화면 밖, scale 없음) */}
      <div
        style={{ position: "fixed", left: "-10000px", top: 0, zIndex: -1 }}
        aria-hidden="true"
      >
        <SLEReportPage1 ref={ref1} {...reportProps} />
        <SLEReportPage2
          ref={ref2}
          sectionTitle="언어 이해 및 표현"
          rows={categoryA}
          clientName={info.name}
          pageLabel="1/2"
        />
        <SLEReportPage2
          ref={ref3}
          sectionTitle="말 및 문해"
          rows={categoryB}
          clientName={info.name}
          pageLabel="2/2"
        />
      </div>

      {/* 저장 진행/완료 모달 */}
      <Modal
        open={saveModal.status !== "idle"}
        closable={saveModal.status !== "saving"}
        maskClosable={false}
        keyboard={saveModal.status !== "saving"}
        onCancel={closeSaveModal}
        title={
          saveModal.status === "saving"
            ? "저장 중"
            : saveModal.status === "success"
            ? "저장 완료"
            : "저장 실패"
        }
        footer={
          saveModal.status === "saving" ? null : (
            <Button type="primary" onClick={closeSaveModal}>
              확인
            </Button>
          )
        }
      >
        {saveModal.status === "saving" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, fontSize: 15 }}>
              저장 중입니다. 잠시만 기다려주세요...
            </p>
          </div>
        )}
        {saveModal.status === "success" && (
          <div style={{ padding: "8px 0", fontSize: 15 }}>
            <CheckCircleFilled style={{ color: "#52c41a", marginRight: 8 }} />
            저장이 완료되었습니다.
          </div>
        )}
        {saveModal.status === "error" && (
          <div style={{ padding: "8px 0", fontSize: 15 }}>
            <CloseCircleFilled style={{ color: "#ff4d4f", marginRight: 8 }} />
            {saveModal.message}
          </div>
        )}
      </Modal>
    </div>
  );
}

const BarContainer = styled.div`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? "column" : "row")};
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px 24px;
  box-sizing: border-box;
  background-color: var(--bg-disable);
  gap: 16px;
`;

const SideComponent = styled.div`
  flex: 1;
  display: flex;
  justify-content: ${({ align }) => align || "flex-start"};
  width: 100%;
`;

const ReportName = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const SummaryArea = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 32px 24px 8px;
  box-sizing: border-box;
  width: 100%;
`;

const SummaryTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #003366;
  margin-bottom: 16px;
`;

const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #d9dce3;
    padding: 14px;
    text-align: center;
    font-size: 16px;
  }
  th {
    background-color: #eef0f6;
    font-weight: 600;
  }
  td {
    font-weight: 600;
  }
`;

const PreviewArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  box-sizing: border-box;
  background-color: #f0f2f5;
`;

export default Analysis;
